import { useClubImportNudge } from "@/components/features/ClubImport/useClubImportNudge";
import { CustomizeSquadDialog } from "@/components/features/SquadBuilder/components/CustomizeSquadDialog/CustomizeSquadDialog";
import {
  trackAddedPlayerToEvolutionBuilder,
  trackViewContent,
} from "@/events/squadBuilderMixpanel";
import { useEmptyClubGuard } from "@/features/MyClub/EmptyClub/useEmptyClubGuard";
import { useSticky } from "@/hooks/useSticky";
import { cn } from "@/lib/utils";
import { UpdateFilterType } from "@/pages/Players/FiltersPanel/filterTypes";
import { addPlayerToEvolutionBuilder } from "@/redux/evolutions/evolutionsBuilderSlice";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import {
  MetaRatingsPlayer,
  generateNavigationUrl,
  playersSelector,
  versionUrlSlug,
} from "@/redux/players/playersSlice";
import { Tactic } from "@/redux/tactics/tacticsSlice";
import { safeGetItem, safeSetItem } from "@/utils/safeStorage";
import { evolutionPathPair } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { promptClubImport } from "@repo/shared/redux";
import { usesClubPool } from "@repo/shared/redux";
import { useCreditGate } from "@repo/shared/redux";
import { useClubImportFeature } from "@repo/shared/redux";
import { MetaRating } from "@repo/shared/types";
import { captureMessage } from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";

import { deleteSavedSquad } from "../../../api/privateRequests/squad-builder/delete-squad";
import { getMetaRatings } from "../../../api/privateRequests/squad-builder/get-meta-ratings";
import { CenterPanel } from "../../../components/features/SquadBuilder/CenterPanel/CenterPanel";
import { LeftPanel } from "../../../components/features/SquadBuilder/LeftPanel/LeftPanel";
import { RightPanel } from "../../../components/features/SquadBuilder/RightPanel/RightPanel";
import { FilterKey } from "../../../components/features/SquadBuilder/components/FiltersBar/FiltersBar";
import { Options as PlayerActiveBarOptions } from "../../../components/features/SquadBuilder/components/PlayerActiveBarMobile/PlayerActiveBarMobile";
import { loadedContentSelector } from "../../../redux/loadedContent/loadedContentSlice";
import { addAlert, showModal } from "../../../redux/message/messageSlice";
import {
  activeFilterCountSelector,
  activeFiltersSelector,
  addPlayerOrChangeRole,
  addSquad,
  buildSquads,
  buildSquadsWithBestFormation,
  clearAllFilters,
  clearCustomFilter,
  clearFilter,
  clearPlayerFilters,
  clearRecommendedPlayers,
  clearSquad,
  defaultSquadBuilderSelector,
  exitPickSquad,
  fetchRecommendedPlayers,
  fetchSpecificSquad,
  fetchSquads,
  getCodeFromTactic,
  getPlayStyleFilterOptions,
  getTacticFromCode,
  hasDefaultFiltersSelector,
  importSquad,
  movePlayer,
  numberOfPlayersWithFiltersSelector,
  removePlayer,
  removeTempSquad,
  resetRole,
  saveSquad,
  selectChemStyle,
  selectLiveEvent,
  setChosenTactic,
  setSaveSquadStatusToIdle,
  sortBuiltSquads,
  switchLeftPanelMode,
  switchRightPanelMode,
  updateBestFormation,
  updateBudget,
  updateChangingPitchItemOnIndex,
  updateCustomFilter,
  updateExcludeIconsHeroes,
  updateFormation,
  updateHasChangedTactic,
  updateIncludeLoanPlayers,
  updateLoadedSquadIndex,
  updateManager,
  updateMaxRating,
  updateMinRating,
  updatePlayersMetaRatingsForAllRoles,
  updateScope,
  updateSelectedClubs,
  updateSelectedCountries,
  updateSelectedLeagues,
  updateSelectedMetals,
  updateSelectedPnPs,
  updateSelectedVersions,
  updateStatusesToIdle,
} from "../../../redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import {
  Scope,
  Squad,
  SquadBuilderPlayerType,
} from "../../../redux/squadBuilder/squadBuilderUtils";
import { AppDispatch } from "../../../redux/store";
import { User, usedFeature } from "../../../redux/user/userSlice";
import { MobileWrapper } from "../MobileWrapper/MobileWrapper";

// How long the filters spotlight stays mounted; matches the hint-fade keyframe.
const FILTERS_HINT_MS = 7000;
// Set once the spotlight has run in this browser; it never runs again.
const FILTERS_HINT_SHOWN_KEY = "squad_builder_filters_hint_shown";

type Props = {
  user: User;
  desktopMode: boolean;
  tabletOrLargerMode: boolean;
};

export const DefaultSquadBuilder = ({
  user,
  desktopMode,
  tabletOrLargerMode,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation(["squadBuilder", "common"]);
  const clubAccess = useClubImportFeature("see-own-players");
  const creditGate = useCreditGate("build-squad-club");
  const nudgeOnBuild = useClubImportNudge("squad-builder-build");
  const nudgeOnInsert = useClubImportNudge("squad-builder-insert");
  // Hand-placed players this visit; the nudge waits for the second one.
  const insertsRef = useRef({ count: 0, nudged: false });
  const { shouldPromptEmptyClub, promptEmptyClub } = useEmptyClubGuard({
    section: "Squad Builder",
    subsection: "Normal",
  });

  const {
    tempSquad,
    squad,
    benchItems,
    tempBenchItems,
    statusBuildSquad,
    statusBuildSquadWithBestFormation,
    statusGetRecommendedPlayers,
    budget,
    excludeIconsHeroes,
    ratingFilter,
    scope,
    selectedCountries,
    selectedLeagues,
    selectedClubs,
    selectedVersions,
    selectedMetals,
    changingPitchItemOnIndex,
    recommendedPlayers,
    rightPanelMode,
    builtSquads,
    statusImportSquad,
    playerFilters,
    leftPanelMode,
    chosenTactic,
    statusSaveSquad,
    statusLoadSpecficSquad,
    statusFetchSavedSquads,
    statusInsertTacticCode,
    savedSquads,
    loadedSaveSquadIndex,
    selectedPnPs,
    tempFormation,
    includeLoanPlayers,
    isBestFormationOn,
    tempChosenTactic,
  } = useSelector(defaultSquadBuilderSelector);
  const { chemistryProfilesStatus, formations, versionAssets, liveEvents } =
    useSelector(loadedContentSelector);
  const { initialPlayerDataStatus, playerRoles } = useSelector(playersSelector);

  const numberOfPlayersWithFilters = useSelector(
    numberOfPlayersWithFiltersSelector,
  );
  const [showLeftPanelMobile, setShowLeftPanelMobile] = useState(false);
  const [showRightPanelMobile, setShowRightPanelMobile] = useState(false);
  const [tacticLoadedFirstTime, setTacticLoadedFirstTime] = useState(false);
  const [currentBuiltSquadsIndex, setCurrentBuildSquadsIndex] = useState(-1);
  const [playerActiveBar, setPlayerActiveBar] =
    useState<PlayerActiveBarOptions>("none");
  const showLeftPanel = desktopMode || showLeftPanelMobile;
  const showRightPanel = desktopMode || showRightPanelMobile;
  const navigate = useNavigate();
  const [showModals, setShowModals] = useState<"save">(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const topToPage = useSelector(topToPageSelector);
  const activeFilters = useSelector(activeFiltersSelector);
  const hasDefaultFilters = useSelector(hasDefaultFiltersSelector);
  const activeFilterCount = useSelector(activeFilterCountSelector);

  // A Build with no filter set asks for them first, in the dialog below.
  const [showCustomizeDialog, setShowCustomizeDialog] = useState(false);
  // The first squad picked after the dialog was shown gets the filters
  // spotlight, once per browser. Armed when the dialog opens, spent on the pick.
  const [filtersHint, setFiltersHint] = useState(false);
  const filtersHintArmedRef = useRef(false);
  const showFiltersHint = () => {
    if (!filtersHintArmedRef.current) return;
    if (safeGetItem(FILTERS_HINT_SHOWN_KEY)) return;
    safeSetItem(FILTERS_HINT_SHOWN_KEY, "1");
    // The panel needs a beat to switch back to its input mode first.
    setTimeout(() => setFiltersHint(true), 300);
    setTimeout(() => setFiltersHint(false), 300 + FILTERS_HINT_MS);
  };
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
    offset: topToPage.value,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];

  // Always land on the chemistry view, even if tactics was left open.
  useEffect(() => {
    dispatch(switchLeftPanelMode("chem-overview"));
  }, [dispatch]);

  useEffect(() => {
    if (changingPitchItemOnIndex === -1) {
      setPlayerActiveBar("none");
    }
  }, [changingPitchItemOnIndex]);

  const handleLoadSquads = () => dispatch(fetchSquads());

  useEffect(() => {
    if (user.uuid && statusFetchSavedSquads === "idle") {
      handleLoadSquads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uuid, statusFetchSavedSquads, statusSaveSquad, dispatch]);

  useEffect(() => {
    if (initialPlayerDataStatus === "fulfilled") {
      dispatch(getPlayStyleFilterOptions());
    }
  }, [dispatch, initialPlayerDataStatus]);

  useEffect(() => {
    if (statusSaveSquad === "fulfilled") {
      dispatch(
        addAlert({
          type: "success",
          description: t("alerts.savedSquad"),
        }),
      );
      dispatch(setSaveSquadStatusToIdle());
    }
  }, [statusSaveSquad, dispatch]);

  useEffect(() => {
    const fetchSharedSquad = async (id: string) => {
      try {
        await dispatch(
          fetchSpecificSquad({ squadId: id, sharedOrSaved: "shared" }),
        );
        dispatch(
          addAlert({
            type: "success",
            description: t("alerts.loadedSquad"),
          }),
        );
      } catch (e) {
        dispatch(
          addAlert({
            type: "error",
            description: t("alerts.loadFailed"),
          }),
        );
      } finally {
        setSearchParams("", { replace: true });
      }
    };

    const squadId = searchParams.get("id");
    if (squadId && chemistryProfilesStatus === "fulfilled") {
      fetchSharedSquad(squadId);
    }
  }, [searchParams, setSearchParams, dispatch, chemistryProfilesStatus]);

  useEffect(() => {
    const fetchSavedSquadFromLogin = async (id: string) => {
      try {
        await dispatch(
          fetchSpecificSquad({ squadId: id, sharedOrSaved: "saved" }),
        );
        dispatch(
          addAlert({
            type: "success",
            description: t("alerts.loadedSquad"),
          }),
        );
        const res = await dispatch(fetchSquads());
        dispatch(updateLoadedSquadIndex((res.payload as []).length - 1));
      } catch (e) {
        dispatch(
          addAlert({
            type: "error",
            description: t("alerts.loadFailed"),
          }),
        );
      } finally {
        setSearchParams("", { replace: true });
      }
    };

    const squadId = searchParams.get("saved_squad_id");
    if (squadId && user.uuid && chemistryProfilesStatus === "fulfilled") {
      fetchSavedSquadFromLogin(squadId);
    }
  }, [searchParams, setSearchParams, dispatch, chemistryProfilesStatus, user]);

  /*
    An imported club does not guarantee an active squad — the profile carries
    `lastImportedAt`, not the squad — so the only certain answer we hold is an
    empty club. Everything else has to ask, and a failed ask means the same
    thing: there is nothing to load, so send them to the import.
  */
  const loadActiveSquad = useCallback(async () => {
    if (!clubAccess.allowed) {
      if (clubAccess.reason !== "loading")
        dispatch(
          promptClubImport("see-active-squad", { section: "Squad Builder" }),
        );
      return;
    }
    if (shouldPromptEmptyClub) {
      promptEmptyClub();
      return;
    }
    try {
      await dispatch(importSquad()).unwrap();
    } catch {
      dispatch(
        promptClubImport("see-active-squad", { section: "Squad Builder" }),
      );
    }
  }, [clubAccess, dispatch, shouldPromptEmptyClub, promptEmptyClub]);

  // Set by ?liveEvent=1 until the live events have loaded.
  const liveEventPendingRef = useRef(false);
  /*
    Arriving with ?customize=1 (e.g. My Club's "upgrades for 100K" card): the
    Customize Squad Suggestions dialog opens, with ?budget= and ?scope= filled
    in, so the user only has to press Build Squads.
  */
  useEffect(() => {
    if (searchParams.get("customize") !== "1") return;
    // A fresh start: whatever filters the last visit left are cleared, so
    // only what the link asks for applies.
    dispatch(clearAllFilters());
    if (searchParams.get("bestFormation") !== "1")
      dispatch(updateBestFormation(false));
    const budgetParam = Number(searchParams.get("budget"));
    if (budgetParam > 0) dispatch(updateBudget(budgetParam));
    const scopeParam = searchParams.get("scope");
    if (
      scopeParam === "market" ||
      scopeParam === "both" ||
      scopeParam === "club"
    )
      dispatch(updateScope(scopeParam));
    /*
      ?bestFormation=1 (My Club's "Build best squad" card): an empty pitch
      with Best formation on, so the build picks the formation as well as the
      players, with none of them forced in.
    */
    if (searchParams.get("bestFormation") === "1") {
      dispatch(clearSquad());
      dispatch(updateBestFormation(true));
    }
    // ?liveEvent=1 (My Club's "Be ready for Live Events"): the live event is
    // picked once the live events have loaded, below.
    if (searchParams.get("liveEvent") === "1")
      liveEventPendingRef.current = true;
    setSearchParams("", { replace: true });
    setShowCustomizeDialog(true);
  }, [searchParams, setSearchParams, dispatch]);

  useEffect(() => {
    if (!liveEventPendingRef.current || !liveEvents.length) return;
    liveEventPendingRef.current = false;
    // The latest one: the list runs oldest to newest.
    dispatch(selectLiveEvent(liveEvents[liveEvents.length - 1].id));
  }, [liveEvents, dispatch]);

  // Arriving from "Show Active Squad" on the front page.
  useEffect(() => {
    if (searchParams.get("squad") !== "active") return;
    if (!user.uuid || chemistryProfilesStatus !== "fulfilled") return;
    // Replace, not push: otherwise Back lands on ?squad=active again and
    // imports the squad a second time instead of leaving the page.
    setSearchParams("", { replace: true });
    loadActiveSquad();
  }, [
    searchParams,
    setSearchParams,
    chemistryProfilesStatus,
    user.uuid,
    loadActiveSquad,
  ]);

  const handleAddSquad = (index: number, temp: boolean) => {
    dispatch(addSquad({ index, temp: temp }));
    if (!temp) {
      dispatch(switchRightPanelMode("default"));
      setCurrentBuildSquadsIndex(-1);
      showFiltersHint();
    }
  };

  useEffect(() => {
    dispatch(getCodeFromTactic({ tactic: chosenTactic }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenTactic.teamTactics, chosenTactic.playerRoles]);

  // Used to check if the tactic has changed since the last time it was applied
  useEffect(() => {
    if (!chosenTactic.eaCode) {
      return;
    }

    if (
      statusImportSquad !== "fulfilled" &&
      statusLoadSpecficSquad !== "fulfilled" &&
      statusBuildSquadWithBestFormation !== "fulfilled" &&
      tacticLoadedFirstTime
    ) {
      dispatch(
        updateHasChangedTactic({
          name: t("tactic.customName"),
          // Saved with the squad, so drop the link markup
          description: t("tactic.customDescription").replace(
            /<\/?tactics>/g,
            "",
          ),
        }),
      );
    } else if (
      statusImportSquad === "fulfilled" ||
      statusLoadSpecficSquad === "fulfilled" ||
      statusBuildSquadWithBestFormation === "fulfilled"
    ) {
      dispatch(updateStatusesToIdle());
    }
    setTacticLoadedFirstTime(true);
  }, [chosenTactic.eaCode]);

  // if loaded squad then update has changed tactic to false

  useEffect(() => {
    if (statusBuildSquad === "fulfilled" && !desktopMode) {
      setCurrentBuildSquadsIndex(0);
      handleAddSquad(0, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusBuildSquad, desktopMode]);

  const handleChangePlayerRole = (index: number) => {
    dispatch(updateChangingPitchItemOnIndex(index));
    dispatch(switchRightPanelMode("add-player-role"));
    setPlayerActiveBar("role");
    setShowRightPanelMobile(true);
  };

  const handleRemovePlayerOrRole = (index: number) => {
    if (index > 10 || squad.players[index]?.resourceId) {
      dispatch(removePlayer({ index }));
      dispatch(updateChangingPitchItemOnIndex(-1));
    } else if (tempSquad.players[index]?.resourceId) {
      // fallback in case a temp player is somehow present
      dispatch(removePlayer({ index, temp: true }));
      dispatch(updateChangingPitchItemOnIndex(-1));
    } else {
      dispatch(resetRole(index));
    }
  };

  const handleAddPlayer = (index: number) => {
    dispatch(updateChangingPitchItemOnIndex(index));
    dispatch(switchRightPanelMode("add-player"));
    setPlayerActiveBar("insert");
    setShowRightPanelMobile(true);
    if (!desktopMode) {
      window.scrollTo(0, 104);
    }
  };

  const handleClickedClose = () => {
    if (desktopMode || !squad.players[changingPitchItemOnIndex]?.resourceId) {
      dispatch(updateChangingPitchItemOnIndex(-1));
    } else {
      setPlayerActiveBar("none");
    }
    dispatch(switchRightPanelMode("default"));
    dispatch(clearRecommendedPlayers());
    setShowRightPanelMobile(false);
  };

  const handleClickedClosePlayerFiltersMode = () => {
    dispatch(switchRightPanelMode("default"));
    setShowRightPanelMobile(false);
    dispatch(updateChangingPitchItemOnIndex(-1));
  };

  const handleClickedCloseMoreFiltersMode = () => {
    dispatch(switchRightPanelMode("default"));
  };

  const handleCloseSuggestedSquadsMode = () => {
    dispatch(exitPickSquad());
    dispatch(switchRightPanelMode("default"));
    setShowRightPanelMobile(false);
  };

  const handleClickedCloseChemstyleMode = async () => {
    const player = squad.players[changingPitchItemOnIndex];
    if (!player) {
      return;
    }
    dispatch(switchRightPanelMode("default"));
    setShowRightPanelMobile(false);
    dispatch(updateChangingPitchItemOnIndex(-1));
  };

  const handleGetPlayerRecommendations = () => {
    dispatch(fetchRecommendedPlayers());
  };

  const handleDidAddPlayer = async (
    playerRoleId?: number,
    player?: SquadBuilderPlayerType,
    temp?: boolean,
    metaRatings?: MetaRating[],
    focus?: string,
  ) => {
    const changingPlayer = changingPitchItemOnIndex;
    dispatch(
      addPlayerOrChangeRole({
        playerRoleId,
        player,
        temp: tabletOrLargerMode ? temp : false,
        metaRatings,
        focus,
      }),
    );
    if (!temp && player?.resourceId) {
      const inserts = insertsRef.current;
      inserts.count += 1;
      if (inserts.count >= 2 && !inserts.nudged)
        inserts.nudged = nudgeOnInsert({ subsection: "Insert" });
    }
    if (!temp && player?.resourceId && desktopMode) {
      handleClickedClose();
    }
    if (player?.resourceId && !temp && !desktopMode) {
      setShowRightPanelMobile(false);
      setPlayerActiveBar("none");
      if (window.scrollY > 104) {
        window.scrollTo(0, 104);
      } else if (changingPitchItemOnIndex >= 10) {
        window.scrollTo(0, 400);
      }
    }
    const resourceId =
      player?.resourceId || squad.players[changingPlayer]?.resourceId;
    if (!temp && resourceId) {
      const evoSource = player?.evolutionPath?.length
        ? player
        : squad.players[changingPlayer];
      const evolutionPath = evoSource?.evolutionPath;
      const pathwayPath = evoSource?.pathwayPath;
      const evoPathId =
        player?.evoPathId || squad.players[changingPlayer]?.evoPathId;

      try {
        const metaRatingsForAllRoles = await getMetaRatings({
          resourceId,
          evolutionPath,
          pathwayPath,
          evoPathId,
          allChemStyles: 0,
        });

        dispatch(
          updatePlayersMetaRatingsForAllRoles({
            metaRatings: metaRatingsForAllRoles,
            changingPlayerIndex: changingPlayer,
          }),
        );
      } catch (error) {
        captureMessage(error instanceof Error ? error.message : String(error), {
          level: "error",
          extra: {
            resourceId,
            evolutionPath,
            pathwayPath,
            evoPathId,
          },
          tags: {
            component: "handleDidAddPlayer",
          },
        });
      }
    }
  };

  const handleUpdateFormation = (formationId: string, isTemp: boolean) =>
    dispatch(
      updateFormation({
        formationId,
        isTemp: desktopMode ? isTemp : false,
      }),
    );

  // A build with nothing set goes through the Customize Squad Suggestions
  // dialog first (formation, budget, scope).
  const handleClickedBuildSquads = () => {
    if (hasDefaultFilters) {
      filtersHintArmedRef.current = true;
      setShowCustomizeDialog(true);
      return;
    }
    buildSquadsNow();
  };

  const buildSquadsNow = () => {
    const needsClub = usesClubPool(scope);
    if (needsClub && !clubAccess.allowed) {
      if (clubAccess.reason !== "loading") {
        dispatch(
          promptClubImport("build-squad-club", { section: "Squad Builder" }),
        );
      }
      return;
    }
    // An imported club that has since been emptied still passes clubAccess, and
    // building from it returns nothing — say so instead of spending a credit on
    // an empty result. Before the price check, since it costs nothing to learn.
    if (needsClub && shouldPromptEmptyClub) {
      promptEmptyClub();
      return;
    }
    // Club first, then price: "import your club" is the wrong thing to hear
    // when the club is there and the balance isn't, and vice versa.
    if (!creditGate({ qualifying: needsClub })) return;
    if (isBestFormationOn) {
      dispatch(usedFeature({ feature: "BEST_FORMATION" }));
      dispatch(buildSquadsWithBestFormation());
    } else {
      dispatch(usedFeature({ feature: "BUILD_SQUADS" }));
      dispatch(buildSquads());
    }
    // After the build, not instead of it: the nudge rides on the wait.
    nudgeOnBuild({ subsection: "Normal" });
  };

  /*
    Building with club players needs a club to build from. The credit cost of
    the build itself is enforced by the backend on the call.
  */
  const handleScopeChange = (newScope: Scope) => {
    if (!clubAccess.allowed) {
      if (clubAccess.reason !== "loading")
        dispatch(
          promptClubImport("build-squad-club", { section: "Squad Builder" }),
        );
      return;
    }
    // Building from an empty club returns nothing, so ask for the import
    // instead of taking the scope — dismissing the dialog has to leave the
    // user where they were, not on a scope that cannot build.
    if (newScope !== "market" && shouldPromptEmptyClub) {
      promptEmptyClub();
      return;
    }
    dispatch(updateScope(newScope));
  };

  const handleSelectedCountry = (id: number) =>
    dispatch(updateSelectedCountries(id));

  const handleSelectedLeague = (id: number) =>
    dispatch(updateSelectedLeagues(id));

  const handleSelectedClub = (id: number) => dispatch(updateSelectedClubs(id));

  const handleSelectedPastAndPresent = (id: number) => {
    dispatch(updateSelectedPnPs(id));
  };

  const handleSelectedVersion = (id: number) =>
    dispatch(updateSelectedVersions(id));

  const handleSelectedMetal = (id: number) =>
    dispatch(updateSelectedMetals(id));

  const handleBudgetUpdated = (budget: number) =>
    dispatch(updateBudget(budget));

  const handleExcludeIconsHeroesUpdated = (excludeIconsHeroes: boolean) =>
    dispatch(updateExcludeIconsHeroes(excludeIconsHeroes));

  const handleIncludeLoanPlayersUpdated = (includeLoanPlayers: boolean) => {
    if (!clubAccess.allowed) {
      if (clubAccess.reason !== "loading")
        dispatch(
          promptClubImport("include-loan-players", {
            section: "Squad Builder",
          }),
        );
      return;
    }
    dispatch(updateIncludeLoanPlayers(includeLoanPlayers));
  };

  const handleMinRatingUpdated = (minRating: number) =>
    dispatch(updateMinRating(minRating));

  const handleMaxRatingUpdated = (maxRating: number) =>
    dispatch(updateMaxRating(maxRating));

  const handleMovePlayer = (fromIndex: number, toIndex: number) => {
    dispatch(
      movePlayer({
        fromIndex,
        toIndex,
      }),
    );
  };

  const handleRemoveTempPlayer = () => dispatch(removePlayer({ temp: true }));

  const handleSortBuiltSquads = (sortBy: "meta-rating" | "chemistry") =>
    dispatch(sortBuiltSquads(sortBy));

  const handleRemoveTempSquad = () => dispatch(removeTempSquad());

  const handleSelectManager = (id: number, type: "country" | "league") =>
    dispatch(
      updateManager({
        id,
        type,
      }),
    );

  const handleChangeChemstyle = (index: number) => {
    dispatch(switchRightPanelMode("select-chem-style"));
    dispatch(updateChangingPitchItemOnIndex(index));
  };

  const handleSelectChemstyle = (
    chemstyleId: number,
    metaRatings: MetaRating[],
  ) => {
    dispatch(
      selectChemStyle({
        chemstyleId,
        metaRatings,
      }),
    );
  };

  const handleAddEvoPlayer = (index: number) => {
    dispatch(updateChangingPitchItemOnIndex(index));
    const player = squad.players[index];
    // Placeholder slots have no resourceId; navigating would build a garbage
    // /evolutions/builder/0 URL that the router matches as a player name.
    if (player?.resourceId) {
      trackAddedPlayerToEvolutionBuilder();
      dispatch(
        addPlayerToEvolutionBuilder({
          player: player as MetaRatingsPlayer,
          evolutionChain: player?.evolutionPath || [],
          pathwayChain: player?.pathwayPath,
        }),
      );

      const navLink = generateNavigationUrl({
        name: player?.cardName || "",
        resourceId: player?.resourceId || 0,
        playerRoleId: player?.playerRoleId || 0,
        evoPathId: player?.evoPathId || 0,
        evolutionPath: player?.evolutionPath || [],
        pathwayPath: player?.pathwayPath,
        basePath: "/evolutions/builder",
        versionSlug: versionUrlSlug(versionAssets[player?.versionId]),
      });
      navigate(`${navLink}&from=squad-builder`);
    }
  };

  const handleAddPlayerFilters = (index: number) => {
    dispatch(updateChangingPitchItemOnIndex(index));
    if (desktopMode) {
      dispatch(switchRightPanelMode("add-player-filters"));
    } else {
      handlePlayerActiveBar("filter");
    }
  };

  const handleClearPlayerFilters = async (index: number) => {
    dispatch(clearPlayerFilters(index));
    dispatch(getPlayStyleFilterOptions());
  };

  const handleUpdateFilter = (f: UpdateFilterType) => {
    dispatch(updateCustomFilter(f));
  };

  const handleClearCustomFilter = (key: string) => {
    dispatch(clearCustomFilter(key));
  };

  const handleClearFilter = (key: FilterKey) => {
    dispatch(clearFilter(key));
  };

  const handleSwitchLeftPanelMode = (type: "tactics" | "overview") => {
    if (type === "tactics") {
      dispatch(switchLeftPanelMode("tactics"));
    } else {
      dispatch(switchLeftPanelMode("chem-overview"));
    }
  };

  const handleOnClickPlayerMobile = (index: number) => {
    dispatch(
      updateChangingPitchItemOnIndex(
        changingPitchItemOnIndex === index ? -1 : index,
      ),
    );
    setPlayerActiveBar("none");
  };

  const handleOnClickPitchMobile = () =>
    dispatch(updateChangingPitchItemOnIndex(-1));

  const handleClickSquadSummaryMobile = () => {
    setShowLeftPanelMobile(
      leftPanelMode !== "chem-overview" || !showLeftPanelMobile,
    );
    setShowRightPanelMobile(false);
    setPlayerActiveBar("none");
    dispatch(switchLeftPanelMode("chem-overview"));
  };

  const handleClickFiltersSummaryMobile = () => {
    setShowRightPanelMobile(
      rightPanelMode !== "default" || !showRightPanelMobile,
    );
    setShowLeftPanelMobile(false);
    setPlayerActiveBar("none");
    dispatch(switchRightPanelMode("default"));
  };

  const handleClickTacticSummaryMobile = () => {
    setShowLeftPanelMobile(leftPanelMode !== "tactics" || !showLeftPanelMobile);
    setShowRightPanelMobile(false);
    setPlayerActiveBar("none");
    dispatch(switchLeftPanelMode("tactics"));
  };

  const handleOnClickPlayer = (playerIndex: number) => {
    let player: SquadBuilderPlayerType;
    if (playerIndex > 10) {
      player = benchItems[playerIndex - 11];
    } else {
      player = squad.players[playerIndex];
    }
    const {
      cardName,
      resourceId,
      playerRoleId,
      eaPlayerId,
      evoPathId,
      evolutionPath,
      pathwayPath,
      chemstyleId,
    } = player;
    const chemistry = squad.chemistry.playersChemistry?.[playerIndex] || 0;

    trackViewContent();

    if (evolutionPath?.length > 0) {
      dispatch(
        addPlayerToEvolutionBuilder({ player: player as MetaRatingsPlayer }),
      );
    }

    const versionSlug = versionUrlSlug(versionAssets[player?.versionId]);
    const navLink = generateNavigationUrl({
      name: cardName,
      resourceId,
      playerRoleId,
      chemistry,
      eaPlayerId,
      evoPathId,
      basePath:
        evolutionPath?.length > 0 ? "/evolutions/builder/details" : "/players",
      evolutionPath,
      pathwayPath,
      chemstyleId,
      versionSlug,
    });
    navigate(navLink, {
      state: {
        chemistryContext: chemistry,
        chemStyleContext: chemstyleId,
      },
    });
  };

  const noSelectedActivePlayerBarItem = () => {
    setPlayerActiveBar("none");
    setShowRightPanelMobile(false);
    setShowLeftPanelMobile(false);
  };

  const handlePlayerActiveBar = (option: PlayerActiveBarOptions) => {
    switch (option) {
      case "role":
        if (playerActiveBar === "role") {
          noSelectedActivePlayerBarItem();
        } else {
          setShowRightPanelMobile(true);
          setPlayerActiveBar("role");
          dispatch(switchRightPanelMode("add-player-role"));
        }
        break;
      case "chemistry":
        if (playerActiveBar === "chemistry") {
          noSelectedActivePlayerBarItem();
        } else {
          setShowRightPanelMobile(true);
          setPlayerActiveBar("chemistry");
          dispatch(switchRightPanelMode("select-chem-style"));
        }
        break;
      case "insert":
        if (playerActiveBar === "insert") {
          noSelectedActivePlayerBarItem();
        } else {
          setShowRightPanelMobile(true);
          setPlayerActiveBar("insert");
          dispatch(switchRightPanelMode("add-player"));
        }
        break;
      case "details":
        if (playerActiveBar === "details") {
          noSelectedActivePlayerBarItem();
        } else {
          setPlayerActiveBar("details");
          handleOnClickPlayer(changingPitchItemOnIndex);
        }
        break;
      case "evolve":
        if (playerActiveBar === "evolve") {
          noSelectedActivePlayerBarItem();
        } else {
          setPlayerActiveBar("evolve");
          // handleAddEvoPlayer dispatches the same player, guarded and with its
          // evolution chain, so dispatching here first only added a placeholder.
          handleAddEvoPlayer(changingPitchItemOnIndex);
        }
        break;
      case "filter":
        if (playerActiveBar === "filter") {
          noSelectedActivePlayerBarItem();
        } else {
          setShowRightPanelMobile(true);
          setPlayerActiveBar("filter");
          dispatch(switchRightPanelMode("add-player-filters"));
        }
        break;
    }
  };

  const handleCurrentBuiltSquadsIndex = (index: number) => {
    let newIndex: number;
    if (index < 0) {
      newIndex = builtSquads.length - 1;
    } else if (index === builtSquads.length) {
      newIndex = 0;
    } else {
      newIndex = index;
    }
    handleAddSquad(newIndex, true);
    setCurrentBuildSquadsIndex(newIndex);
  };

  // Mobile only: closing the built squads without taking one. On a phone the
  // panel is closed, so this is also the moment to point at the filters.
  const handleClickedCancelBuildSquads = () => {
    dispatch(exitPickSquad());
    setCurrentBuildSquadsIndex(-1);
    dispatch(removeTempSquad());
    if (!desktopMode) showFiltersHint();
  };

  const defineSquadData = ({
    chosenTactic,
    benchItems,
    squad,
  }: {
    chosenTactic: Tactic;
    benchItems: SquadBuilderPlayerType[];
    squad: Squad;
  }) => {
    return {
      formation: chosenTactic.teamTactics.formationId,
      players: [
        ...squad.players.map((player) => ({
          playerRoleId: player.playerRoleId,
          resourceId: player.resourceId,
          ...evolutionPathPair(player.evolutionPath, player.pathwayPath),
          evoPathId: player.evoPathId,
        })),
        ...benchItems.map((player) => ({
          playerRoleId: player.playerRoleId,
          resourceId: player.resourceId,
          ...evolutionPathPair(player.evolutionPath, player.pathwayPath),
          evoPathId: player.evoPathId,
        })),
      ],
      manager: {
        countryId: squad.manager?.country,
        leagueId: squad.manager?.league,
      },
      tacticId: "",
      tactic: {
        name: chosenTactic.name,
        description: chosenTactic.description,
        teamTactics: {
          buildUpStyle: chosenTactic.teamTactics.buildUpStyle,
          formationId: chosenTactic.teamTactics.formationId,
          defensiveApproach: chosenTactic.teamTactics.defensiveApproach,
          lineHeight: chosenTactic.teamTactics.lineHeight,
        },
        logoUrl: chosenTactic.logoUrl,
        playerRoles: chosenTactic.playerRoles.map((p) => ({
          playerRoleId: p.playerRoleId,
          focus: p.focus,
        })),
      },
    };
  };

  const handleClickPitchButton = async (
    action: "clear" | "import" | "save" | "share" | "overwrite",
  ) => {
    switch (action) {
      case "clear":
        dispatch(clearSquad());
        break;
      case "import":
        loadActiveSquad();
        break;
      case "save":
        // Overwrite squad intead of showModal
        const saveSquadData = defineSquadData({
          chosenTactic,
          benchItems,
          squad,
        });
        dispatch(showModal({ modalType: "save-squad", data: saveSquadData }));
        break;
      case "overwrite":
        await dispatch(saveSquad({}));
        break;
      case "share":
        const shareSquadData = defineSquadData({
          chosenTactic,
          benchItems,
          squad,
        });
        dispatch(
          showModal({
            modalType: "share-squad",
            data: shareSquadData,
          }),
        );
        break;
    }
  };

  const handleCloseModal = () => {
    setShowModals(undefined);
  };

  const handleLoadSquad = (index: number) => {
    dispatch(
      fetchSpecificSquad({
        index,
        sharedOrSaved: "saved",
      }),
    );
  };

  const handleAddNewSquad = () => {
    dispatch(updateLoadedSquadIndex(undefined));
  };

  const handleDeleteSquad = async (squadId: string) => {
    await deleteSavedSquad(squadId);
    dispatch(updateLoadedSquadIndex(undefined));
    handleLoadSquads();
  };

  const handleGetTacticFromCode = (code: string) => {
    dispatch(getTacticFromCode({ code }));
  };

  const handleCopy = (eaCode: string) => {
    navigator.clipboard.writeText(eaCode).then(
      () => {
        dispatch(
          addAlert({
            title: t("common:success"),
            description: t("common:copiedToClipboard"),
            type: "success",
          }),
        );
      },
      (err) => {
        console.error("Failed to copy: ", err);
      },
    );
  };

  const handleChangeTeamTactic = (key: string, value: string | number) => {
    if (chosenTactic) {
      dispatch(
        setChosenTactic({
          tactic: {
            ...chosenTactic,
            teamTactics: { ...chosenTactic.teamTactics, [key]: value },
          },
          fromTacticsPage: false,
        }),
      );
    }
  };

  const handleClickMoreFilters = () =>
    dispatch(switchRightPanelMode("add-more-filters"));

  const handleClearAllFilters = () => dispatch(clearAllFilters());

  const handlePositionAndRoleChanged = (
    position: string,
    roleId: number,
    metaRatings: MetaRating[],
  ) => {
    dispatch(
      addPlayerOrChangeRole({ position, playerRoleId: roleId, metaRatings }),
    );
  };

  const handleBestFormationChange = (isBestFormation: boolean) => {
    dispatch(updateBestFormation(isBestFormation));
  };

  return (
    <MobileWrapper
      shouldWrap={!desktopMode}
      squad={squad}
      tempSquad={tempSquad}
      benchItems={benchItems}
      tactic={tempChosenTactic ?? chosenTactic}
      activeBar={changingPitchItemOnIndex === -1 ? "default" : "playerActive"}
      playerActiveBar={playerActiveBar}
      handlePlayerActiveBar={handlePlayerActiveBar}
      onClickSquadSummaryMobile={handleClickSquadSummaryMobile}
      onClickFiltersSummaryMobile={handleClickFiltersSummaryMobile}
      onClickTacticSummaryMobile={handleClickTacticSummaryMobile}
      handleSelectManager={handleSelectManager}
      statusImportSquad={statusImportSquad}
      changingPitchItemOnIndex={changingPitchItemOnIndex}
      activeFilters={activeFilters}
      highlightFilters={filtersHint}
      activeMenu={
        showLeftPanelMobile
          ? leftPanelMode === "tactics"
            ? "tactics"
            : "chem-overview"
          : showRightPanelMobile
            ? "default"
            : "none"
      }
      handleClickPitchButton={handleClickPitchButton}
      currentBuiltSquadsIndex={currentBuiltSquadsIndex}
      hasLoadedSquad={loadedSaveSquadIndex !== undefined}
    >
      <div ref={refTopBar} />
      <div
        className={cn(
          "flex justify-center touch-device:w-auto touch-device:min-w-[1100px] desktop:min-h-[calc(115vh)] touch-device:max-h-[1200px] max-w-[2000px] mx-auto relative",
        )}
      >
        {showLeftPanel && (
          <LeftPanel
            formations={formations}
            squad={squad}
            tempSquad={tempSquad}
            chosenTactic={tempChosenTactic ?? chosenTactic}
            desktopMode={desktopMode}
            leftPanelMode={leftPanelMode}
            handleSwitchLeftPanelMode={handleSwitchLeftPanelMode}
            handleCopy={() => handleCopy(chosenTactic?.eaCode)}
            handleChangeBuildUpStyle={(buildUpStyle) =>
              handleChangeTeamTactic("buildUpStyle", buildUpStyle)
            }
            handleChangeDefensiveApproach={(defensiveApproach) =>
              handleChangeTeamTactic("defensiveApproach", defensiveApproach)
            }
            handleChangeFormation={(formationId: string) =>
              handleChangeTeamTactic("formationId", formationId)
            }
            handleChangeLineHeight={(lineHeight) =>
              handleChangeTeamTactic("lineHeight", lineHeight)
            }
            handleGetTacticFromCode={handleGetTacticFromCode}
            statusInsertTacticCode={statusInsertTacticCode}
            scrollPastTopBar={scrollPastTopBar}
          />
        )}
        <CenterPanel
          squad={squad}
          tempSquad={tempSquad}
          benchItems={benchItems}
          tempBenchItems={tempBenchItems}
          statusBuildSquad={statusBuildSquad}
          changingPitchItemOnIndex={changingPitchItemOnIndex}
          handleAddPlayer={handleAddPlayer}
          handleMovePlayer={handleMovePlayer}
          handleChangePlayerRole={handleChangePlayerRole}
          rightPanelMode={rightPanelMode}
          handleRemovePlayerOrRole={handleRemovePlayerOrRole}
          handleAddPlayerFilters={handleAddPlayerFilters}
          playerRoles={playerRoles}
          playerFilters={playerFilters}
          statusImportSquad={statusImportSquad}
          handleSelectManager={handleSelectManager}
          handleChangeChemstyle={handleChangeChemstyle}
          handleAddEvoPlayer={handleAddEvoPlayer}
          handleCloseLoadingModal={handleCloseSuggestedSquadsMode}
          handleOnClickPlayer={handleOnClickPlayer}
          handleOnClickPlayerMobile={handleOnClickPlayerMobile}
          tabletOrLargerMode={tabletOrLargerMode}
          desktopMode={desktopMode}
          mobileHideCenter={
            (showLeftPanelMobile || showRightPanelMobile) && !tabletOrLargerMode
          }
          // Phones toggle it on the pitch, with the panel closed
          tacticsView={
            (showLeftPanel || !tabletOrLargerMode) &&
            leftPanelMode === "tactics"
          }
          handleClickedBuildSquads={handleClickedBuildSquads}
          disablePlayerInteractions={currentBuiltSquadsIndex >= 0}
          handleOnClickPitchMobile={
            desktopMode ? undefined : handleOnClickPitchMobile
          }
          handleClickPitchButton={handleClickPitchButton}
          handleCloseModal={handleCloseModal}
          showModals={showModals}
          chosenTactic={chosenTactic}
          currentBuiltSquadsIndex={currentBuiltSquadsIndex}
          changeBuiltSquadsIndex={handleCurrentBuiltSquadsIndex}
          totalAmountOfBuiltSquads={builtSquads.length || 0}
          tempFormation={tempFormation}
          hasLoadedSquad={loadedSaveSquadIndex !== undefined}
          isBestFormationOn={isBestFormationOn}
          handleEditIncludedPlayers={handleClickFiltersSummaryMobile}
          handleCloseSuggestedSquads={handleClickedCancelBuildSquads}
          handleChoseSquad={() => {
            handleAddSquad(currentBuiltSquadsIndex, false);
          }}
        />
        {showRightPanel && (
          <RightPanel
            statusBuildSquad={statusBuildSquad}
            budget={budget}
            excludeIconsHeroes={excludeIconsHeroes}
            ratingFilter={ratingFilter}
            scope={scope}
            selectedCountries={selectedCountries}
            selectedLeagues={selectedLeagues}
            selectedClubs={selectedClubs}
            selectedPnPs={selectedPnPs}
            selectedVersions={selectedVersions}
            playersToInclude={[]}
            selectedMetals={selectedMetals}
            recommendedPlayers={recommendedPlayers}
            changingPitchItemOnIndex={changingPitchItemOnIndex}
            handleDidAddPlayer={handleDidAddPlayer}
            statusGetRecommendedPlayers={statusGetRecommendedPlayers}
            handleClickedClose={handleClickedClose}
            handleClickedClosePlayerFiltersMode={
              handleClickedClosePlayerFiltersMode
            }
            handleClickedCloseMoreFiltersMode={
              handleClickedCloseMoreFiltersMode
            }
            activeFilters={activeFilters}
            numberOfPlayersWithFilters={numberOfPlayersWithFilters}
            handleClearFilter={handleClearFilter}
            handleGetPlayerRecommendations={handleGetPlayerRecommendations}
            playerRoles={playerRoles}
            playerFilters={playerFilters[changingPitchItemOnIndex]}
            handleUpdateFormation={handleUpdateFormation}
            handleClickedBuildSquads={handleClickedBuildSquads}
            rightPanelMode={rightPanelMode}
            handleUpdateFilter={handleUpdateFilter}
            handleClearCustomFilter={handleClearCustomFilter}
            handleClearPlayerFilters={() =>
              handleClearPlayerFilters(changingPitchItemOnIndex)
            }
            handleScopeChange={handleScopeChange}
            handleSelectedCountry={handleSelectedCountry}
            handleSelectedLeague={handleSelectedLeague}
            handleSelectedClub={handleSelectedClub}
            handleSelectedPnP={handleSelectedPastAndPresent}
            handleSelectedMetal={handleSelectedMetal}
            handleSelectedVersion={handleSelectedVersion}
            handleBudgetUpdated={handleBudgetUpdated}
            handleExcludeIconsHeroesUpdated={handleExcludeIconsHeroesUpdated}
            handleMinRatingUpdated={handleMinRatingUpdated}
            handleMaxRatingUpdated={handleMaxRatingUpdated}
            handleRemoveTempPlayer={handleRemoveTempPlayer}
            handleSortBuiltSquads={handleSortBuiltSquads}
            handleRemoveTempSquad={handleRemoveTempSquad}
            handleAddSquad={handleAddSquad}
            builtSquads={builtSquads}
            squad={squad}
            handleClickedCloseSuggestedSquadsMode={
              handleCloseSuggestedSquadsMode
            }
            handleSelectChemstyle={handleSelectChemstyle}
            handleClickedCloseChemstyleMode={handleClickedCloseChemstyleMode}
            tabletOrLargerMode={tabletOrLargerMode}
            desktopMode={desktopMode}
            savedSquads={savedSquads}
            loadedSaveSquadIndex={loadedSaveSquadIndex}
            handleLoadSquad={handleLoadSquad}
            handleAddNewSquad={handleAddNewSquad}
            handleDeleteSquad={handleDeleteSquad}
            chosenTactic={chosenTactic}
            handleClickMoreFilters={handleClickMoreFilters}
            scrollPastTopBar={scrollPastTopBar}
            benchItems={benchItems}
            handlePositionAndRoleChanged={handlePositionAndRoleChanged}
            includeLoanPlayers={includeLoanPlayers}
            handleIncludeLoanPlayersUpdated={handleIncludeLoanPlayersUpdated}
            handleBestFormationChange={handleBestFormationChange}
            isBestFormationOn={isBestFormationOn}
            activeFilterCount={activeFilterCount}
            handleClearAllFilters={handleClearAllFilters}
            highlightFilters={filtersHint}
          />
        )}
      </div>
      {filtersHint &&
        createPortal(
          <div
            className="fixed inset-0 z-[105] bg-black/65 motion-safe:animate-hint-fade"
            onClick={() => setFiltersHint(false)}
          />,
          document.body,
        )}
      <CustomizeSquadDialog
        isOpen={showCustomizeDialog}
        onClose={() => setShowCustomizeDialog(false)}
        onBuild={buildSquadsNow}
        selectedFormation={chosenTactic.teamTactics.formationId}
        handleUpdateFormation={handleUpdateFormation}
        budget={budget}
        handleBudgetUpdated={handleBudgetUpdated}
        scope={scope}
        handleScopeChange={handleScopeChange}
        canUseClub={clubAccess.allowed}
      />
    </MobileWrapper>
  );
};
